import { Transition } from '@headlessui/react'
import {
  CheckIcon,
  ChevronDoubleRightIcon,
  ChevronDownIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  LinkIcon,
  PencilSquareIcon,
  TrashIcon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline'
import { PaperAirplaneIcon, PlusIcon, StopIcon } from '@heroicons/react/24/solid'
import { format, formatDistanceToNow } from 'date-fns'
import { Tooltip } from 'flowbite-react'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import MessagingIcon from '../../components/Nav/icons/icon-messaging'
import { useAppSelector } from '../../shared/redux/hooks'
import {
  archivePersonalThreadApi,
  deletePersonalThreadApi,
  deleteSharedThreadApi,
  getChatMessagesApi,
  getPersonalHistoryApi,
  getPersonalThreadApi,
  getSharedThreadApi,
  isChatThreadDeletedError,
  renamePersonalThreadApi,
  type ChatMessage,
  type ChatThreadSummary,
} from './chatApi'
import { buildPageContext } from './pageContext'
import ChatMarkdown from './ChatMarkdown'
import { useChatStream } from './useChatStream'
import { useTenantViewFocus } from './tenantViewFocus'
import { useOnboardingViewFocus } from './onboardingViewFocus'
import { formatViewContext } from './viewContext'
import { resolveAccountLinkTenantId } from './chatMessageContext'
import { SAGE_CHAT_DRAWER_WIDTH_KEY, useResizableDrawerWidth } from '../../shared/useResizableDrawerWidth'
import { setDockedDrawerState, useDockedDrawerFabRightOffset } from '../../shared/dockedDrawerLayout'
import {
  buildSageSharedDeepLink,
  hasSageSharedDeepLink,
  stripSageDeepLink,
} from './sageDeepLink'

type TabKind = 'personal' | 'shared'

const MODE_TIP_STORAGE_KEY = 'sageChatModeTipDismissed'
const DOCK_STORAGE_KEY = 'sageChatDrawerDocked'

function readDockedPreference(): boolean {
  try {
    return localStorage.getItem(DOCK_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

type DisplayMessage = {
  id: string
  role: 'user' | 'assistant'
  body: string
  authorEmail?: string | null
  toolsUsed?: string[]
  pending?: boolean
  /** Tenant cuid for account linkify — from the turn’s pageContext, survives navigation. */
  linkTenantId?: string
  /** ISO timestamp from API or optimistic local send. */
  createdAt?: string
  /** Local-only outcome for the latest assistant turn. */
  status?: 'stopped' | 'error'
}

type LastTurn = {
  userMessageId: string
  assistantMessageId: string
  text: string
}

function formatThreadWhen(iso: string): { absolute: string; date: string; relative: string } {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return { absolute: '', date: '', relative: '' }
  }
  return {
    absolute: format(date, 'MMM d, yyyy · h:mm a'),
    date: format(date, 'MMM d, yyyy'),
    relative: formatDistanceToNow(date, { addSuffix: true }),
  }
}

function formatMessageForCopy(message: DisplayMessage): string {
  const speaker = message.role === 'user' ? 'You' : 'Sage'
  const tools =
    message.toolsUsed?.length ? `\nTools: ${message.toolsUsed.join(', ')}` : ''
  return `**${speaker}**\n${message.body.trim()}${tools}`
}

function formatChatForCopy(messages: DisplayMessage[]): string {
  return messages
    .filter((m) => m.body.trim())
    .map(formatMessageForCopy)
    .join('\n\n')
}

/** Local user+assistant pair still in flight (pending / stopped / error). */
function takeInFlightLocalTurn(messages: DisplayMessage[]): DisplayMessage[] {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]!
    if (message.role === 'assistant' && (message.pending || message.status === 'error' || message.status === 'stopped')) {
      const start = i > 0 && messages[i - 1]!.role === 'user' ? i - 1 : i
      return messages.slice(start)
    }
  }
  return []
}

async function copyText(text: string): Promise<boolean> {
  if (!text.trim()) return false
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

const CHAT_TOOLTIP_CLASS =
  'text-xs z-[60] pointer-events-none select-none bg-slate-600 text-white dark:bg-slate-950 border-0'

const CHAT_TOOLTIP_THEME = {
  arrow: {
    style: {
      dark: 'bg-slate-600 dark:bg-slate-950',
      light: 'bg-slate-600 dark:bg-slate-950',
      auto: 'bg-slate-600 dark:bg-slate-950',
    },
  },
}

type ChatTooltipProps = {
  content: ReactNode
  placement?: 'top' | 'bottom' | 'left' | 'right'
  children: ReactNode
}

const ChatTooltip: React.FC<ChatTooltipProps> = ({ content, placement = 'bottom', children }) => (
  <Tooltip
    content={content}
    placement={placement}
    className={CHAT_TOOLTIP_CLASS}
    theme={CHAT_TOOLTIP_THEME}
  >
    {children}
  </Tooltip>
)

type CopyIconButtonProps = {
  label: string
  disabled?: boolean
  onCopy: () => Promise<boolean> | boolean
  variant?: 'header' | 'message'
  icon: 'message' | 'chat' | 'link'
}

const CopyIconButton: React.FC<CopyIconButtonProps> = ({
  label,
  disabled,
  onCopy,
  variant = 'message',
  icon,
}) => {
  const [copied, setCopied] = useState(false)

  const handleClick = async () => {
    if (disabled || copied) return
    const ok = await onCopy()
    if (!ok) return
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  const Icon = copied
    ? CheckIcon
    : icon === 'chat'
      ? ClipboardDocumentListIcon
      : icon === 'link'
        ? LinkIcon
        : ClipboardDocumentIcon

  const tip = copied ? 'Copied' : label
  const headerClass =
    'inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors duration-150 hover:bg-slate-200/80 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100'
  const messageClass =
    'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 opacity-0 transition-opacity duration-150 group-hover:opacity-100 hover:bg-slate-200/80 hover:text-slate-700 focus-visible:opacity-100 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-slate-700 dark:hover:text-slate-200'

  return (
    <ChatTooltip content={tip} placement={variant === 'header' ? 'bottom' : 'top'}>
      <span className="inline-flex">
        <button
          type="button"
          disabled={disabled}
          onClick={() => void handleClick()}
          aria-label={copied ? `Copied ${label}` : label}
          className={variant === 'header' ? headerClass : messageClass}
        >
          <Icon className={variant === 'header' ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
        </button>
      </span>
    </ChatTooltip>
  )
}

const ChatDrawer: React.FC = () => {
  const { permissions } = useAppSelector((state) => state.app)
  const canUse = permissions?.includes('admin:chat:use') ?? false
  const canShared = permissions?.includes('admin:chat:shared') ?? false

  const tenant = useAppSelector((state) => state.tenants.tenant)
  const program = useAppSelector((state) => state.programs.program)
  const tariff = useAppSelector((state) => state.tariffs.tariff)
  const tenantViewFocus = useTenantViewFocus()
  const onboardingViewFocus = useOnboardingViewFocus()

  const location = useLocation()
  const navigate = useNavigate()
  const pageContext = useMemo(
    () => buildPageContext(location.pathname, location.search),
    [location.pathname, location.search]
  )

  const focusedAccount = useMemo(() => {
    const san = pageContext.search.serviceAccountNumber
    if (!san || !tenant?.serviceAccounts) return undefined
    return tenant.serviceAccounts.find((sa) => sa.kingAccountNumber === san)
  }, [pageContext.search.serviceAccountNumber, tenant?.serviceAccounts])

  const openAccounts = useMemo(() => {
    if (!tenantViewFocus || tenantViewFocus.tenantId !== pageContext.params.tenantId) return []
    return tenantViewFocus.accounts.filter((a) => a.open)
  }, [tenantViewFocus, pageContext.params.tenantId])

  const viewContext = useMemo(() => {
    const registrationMatches =
      onboardingViewFocus != null &&
      onboardingViewFocus.registrationId === pageContext.params.registrationId
    return formatViewContext(pageContext, {
      tenantId: tenant?.id,
      tenantName: tenant?.name,
      accountNumber: focusedAccount?.kingAccountNumber,
      accountName: focusedAccount?.kingAccountName,
      programId: program?.id,
      programName: program?.name ?? program?.salesforceName ?? undefined,
      includeConductor: permissions?.includes('conductor:read') === true,
      openAccounts,
      registrationId: registrationMatches ? onboardingViewFocus.registrationId : undefined,
      registrationName: registrationMatches ? onboardingViewFocus.businessName : undefined,
      registrationPropertyName: registrationMatches ? onboardingViewFocus.propertyName : undefined,
      registrationUtilityName: registrationMatches ? onboardingViewFocus.utilityName : undefined,
      tariffId: tariff?.cuid,
      tariffSchedule: tariff?.schedule,
      tariffName: tariff?.name,
    })
  }, [
    pageContext,
    tenant?.id,
    tenant?.name,
    focusedAccount,
    program?.id,
    program?.name,
    program?.salesforceName,
    openAccounts,
    onboardingViewFocus,
    permissions,
    tariff?.cuid,
    tariff?.schedule,
    tariff?.name,
  ])

  /** URL entity + display label. Expanded account panels stay in viewContext; they must not retarget shared. */
  const enrichedPageContext = useMemo(
    () => ({ ...pageContext, label: viewContext.label }),
    [pageContext, viewContext.label]
  )

  const [open, setOpen] = useState(false)
  const [docked, setDocked] = useState(false)
  const [tab, setTab] = useState<TabKind>('personal')
  const [personalThreadId, setPersonalThreadId] = useState<string | null>(null)
  const [sharedThreadId, setSharedThreadId] = useState<string | null>(null)
  const [messages, setMessages] = useState<DisplayMessage[]>([])
  const [draft, setDraft] = useState('')
  const [loadingThread, setLoadingThread] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [contextExpanded, setContextExpanded] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [history, setHistory] = useState<ChatThreadSummary[]>([])
  const [renamingThreadId, setRenamingThreadId] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState<
    | { kind: 'personal'; summary: ChatThreadSummary }
    | { kind: 'shared'; threadId: string }
    | null
  >(null)
  const [deletingHistory, setDeletingHistory] = useState(false)
  const [sharedRemoteDeleted, setSharedRemoteDeleted] = useState(false)
  const [lastTurn, setLastTurn] = useState<LastTurn | null>(null)
  const [showModeTip, setShowModeTip] = useState(false)
  const [deepLinkNotice, setDeepLinkNotice] = useState<string | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const historyMenuRef = useRef<HTMLDivElement>(null)
  const userPersistedRef = useRef(false)
  const tabRef = useRef<TabKind>(tab)
  const sendInFlightRef = useRef(false)
  const threadLoadRef = useRef(Promise.resolve())
  const personalThreadIdRef = useRef<string | null>(null)
  const sharedThreadIdRef = useRef<string | null>(null)
  const sharedEntityKeyRef = useRef<string | null>(null)
  const conversationEpochRef = useRef(0)
  const renamingThreadIdRef = useRef<string | null>(null)
  const skipRenameCommitRef = useRef(false)
  const sharedFreshGenRef = useRef(0)
  tabRef.current = tab
  personalThreadIdRef.current = personalThreadId
  sharedThreadIdRef.current = sharedThreadId
  renamingThreadIdRef.current = renamingThreadId
  const sharedEntityKey = enrichedPageContext.sharedEntity
    ? `${enrichedPageContext.sharedEntity.type}:${enrichedPageContext.sharedEntity.id}`
    : null
  sharedEntityKeyRef.current = sharedEntityKey
  /** Only reload personal messages when the personal tab's selected thread changes. */
  const personalThreadIdForLoad = tab === 'personal' ? personalThreadId : null
  const { width: drawerWidth, onResizePointerDown } = useResizableDrawerWidth(SAGE_CHAT_DRAWER_WIDTH_KEY)
  const fabRightOffset = useDockedDrawerFabRightOffset('chat')
  const { send, cancel, streaming, error: streamError, setError: setStreamError } = useChatStream()

  const threadId = tab === 'shared' ? sharedThreadId : personalThreadId

  const switchTab = useCallback(
    (next: TabKind) => {
      setTab((prev) => {
        if (prev === next) return prev
        sharedFreshGenRef.current += 1
        cancel()
        return next
      })
    },
    [cancel]
  )

  useEffect(() => {
    setDocked(readDockedPreference())
  }, [])

  useEffect(() => {
    setDockedDrawerState('chat', { open, docked, width: drawerWidth })
  }, [open, docked, drawerWidth])

  const toggleDocked = () => {
    setDocked((prev) => {
      const next = !prev
      try {
        localStorage.setItem(DOCK_STORAGE_KEY, next ? '1' : '0')
      } catch {
        // ignore
      }
      return next
    })
  }

  useEffect(() => {
    try {
      if (localStorage.getItem(MODE_TIP_STORAGE_KEY) !== '1') {
        setShowModeTip(true)
      }
    } catch {
      // ignore
    }
  }, [])

  const dismissModeTip = () => {
    setShowModeTip(false)
    try {
      localStorage.setItem(MODE_TIP_STORAGE_KEY, '1')
    } catch {
      // ignore
    }
  }
  useEffect(() => {
    if (!historyOpen) return
    const onPointerDown = (event: MouseEvent) => {
      if (historyMenuRef.current != null && !historyMenuRef.current.contains(event.target as Node)) {
        setHistoryOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (renamingThreadIdRef.current) {
        // Let the rename field discard. Consuming Escape here unmounts the input
        // (blur → commitRenameHistory) before its onKeyDown can cancel.
        return
      }
      event.preventDefault()
      event.stopPropagation()
      setHistoryOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [historyOpen])

  useEffect(() => {
    if (!open || historyOpen || deleteConfirm) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      if (streaming) {
        cancel()
        return
      }
      setOpen(false)
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [open, streaming, historyOpen, cancel, deleteConfirm])

  useEffect(() => {
    if (!deleteConfirm || deletingHistory) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      setDeleteConfirm(null)
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [deleteConfirm, deletingHistory])

  const sharedAvailable = canShared && !!enrichedPageContext.sharedEntity

  const sharedRecordLabel = useMemo(() => {
    const entity = enrichedPageContext.sharedEntity
    if (!entity) return null
    if (entity.type === 'tenantAccount') {
      const accountName = focusedAccount?.kingAccountName?.trim()
      return accountName ? `${entity.id} · ${accountName}` : entity.id
    }
    const genericHeadlines = new Set([
      'Tenant',
      'Program',
      'Onboarding registration',
      'Property',
      'User',
    ])
    if (viewContext.headline && !genericHeadlines.has(viewContext.headline)) {
      return viewContext.headline
    }
    return null
  }, [enrichedPageContext.sharedEntity, viewContext.headline, focusedAccount?.kingAccountName])

  const sharedTabLabel = sharedRecordLabel ? `This record (${sharedRecordLabel})` : 'This record'

  useEffect(() => {
    if (!canUse || !hasSageSharedDeepLink(location.search)) return

    setOpen(true)
    if (sharedAvailable) {
      switchTab('shared')
      setDeepLinkNotice(null)
    } else {
      switchTab('personal')
      setDeepLinkNotice(
        canShared
          ? 'This record chat needs a focused tenant, account, or program — opened Just me instead.'
          : 'Shared chat requires permission — opened Just me instead.'
      )
    }

    const nextSearch = stripSageDeepLink(location.search)
    if (nextSearch !== location.search) {
      navigate(
        { pathname: location.pathname, search: nextSearch, hash: location.hash },
        { replace: true }
      )
    }
  }, [
    canUse,
    canShared,
    sharedAvailable,
    location.search,
    location.pathname,
    location.hash,
    navigate,
    switchTab,
  ])

  useEffect(() => {
    if (!sharedAvailable && tab === 'shared') {
      switchTab('personal')
    }
  }, [sharedAvailable, tab, switchTab])

  const prevSharedEntityKeyRef = useRef<string | null>(null)
  // Cancel in-flight stream when the shared entity identity changes while on that tab.
  // Also drop sharedThreadId so Send cannot post the previous record's thread while the
  // UI already shows an empty transcript for the new conversation.
  useEffect(() => {
    const prev = prevSharedEntityKeyRef.current
    prevSharedEntityKeyRef.current = sharedEntityKey
    if (tab !== 'shared') return
    if (prev === sharedEntityKey) return
    if (prev == null && sharedEntityKey != null) {
      // First focused entity after mount — loadThread handles the fetch; no in-flight stream yet.
      return
    }
    cancel()
    setMessages([])
    setLastTurn(null)
    setSharedThreadId(null)
    sharedThreadIdRef.current = null
  }, [sharedEntityKey, tab, cancel])

  useEffect(() => {
    if (!canUse) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key?.toLowerCase() === 'j' && event.metaKey && !event.altKey && !event.shiftKey) {
        event.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [canUse])

  const toDisplay = (msgs: ChatMessage[]): DisplayMessage[] =>
    msgs
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({
        id: m.cuid,
        role: m.role as 'user' | 'assistant',
        body: m.body,
        authorEmail: m.authorEmail,
        toolsUsed: m.toolsUsed,
        createdAt: m.createdAt,
        linkTenantId: resolveAccountLinkTenantId({
          pageContext: m.pageContext,
          markdown: m.body,
        }),
      }))

  const refreshHistory = useCallback(async () => {
    if (!canUse) return
    try {
      const rows = await getPersonalHistoryApi()
      setHistory(rows)
    } catch {
      // History is optional — keep prior list on failure.
    }
  }, [canUse])

  const applyLoadedMessages = (msgs: ChatMessage[]) => {
    setMessages((prev) => {
      const loaded = toDisplay(msgs)
      const inFlight = takeInFlightLocalTurn(prev)
      return inFlight.length === 0 ? loaded : [...loaded, ...inFlight]
    })
  }

  // Load the active tab's thread; ignore stale responses when deps change mid-flight.
  // Depend on sharedEntityKey (type:id), not the sharedEntity object — label / open-account
  // changes recreate that object with the same identity and would flash Loading thread.
  useEffect(() => {
    if (!open || !canUse) return

    let cancelled = false
    let settleLoad: () => void = () => {}
    threadLoadRef.current = new Promise<void>((resolve) => {
      settleLoad = () => {
        resolve()
      }
    })
    setLoadingThread(true)
    setLoadError(null)

    const run = async () => {
      try {
        if (tab === 'shared') {
          if (!sharedEntityKey) {
            if (!cancelled) {
              setSharedThreadId(null)
              sharedThreadIdRef.current = null
              setMessages([])
            }
            return
          }
          const separator = sharedEntityKey.indexOf(':')
          const entityType = sharedEntityKey.slice(0, separator)
          const entityId = sharedEntityKey.slice(separator + 1)
          const thread = await getSharedThreadApi(entityType, entityId)
          if (cancelled) return
          setSharedThreadId(thread.cuid)
          sharedThreadIdRef.current = thread.cuid
          const msgs = await getChatMessagesApi(thread.cuid)
          if (cancelled) return
          applyLoadedMessages(msgs)
        } else if (personalThreadIdForLoad) {
          const msgs = await getChatMessagesApi(personalThreadIdForLoad)
          if (cancelled) return
          applyLoadedMessages(msgs)
          await refreshHistory()
        } else {
          const thread = await getPersonalThreadApi()
          if (cancelled) return
          setPersonalThreadId(thread.cuid)
          personalThreadIdRef.current = thread.cuid
          const msgs = await getChatMessagesApi(thread.cuid)
          if (cancelled) return
          applyLoadedMessages(msgs)
          await refreshHistory()
        }
      } catch (err) {
        if (cancelled) return
        setLoadError(err instanceof Error ? err.message : String(err))
        // Keep personalThreadId on message-fetch failure so we do not loop getPersonalThreadApi
        // or drop a history selection. Always drop sharedThreadId — a failed shared load must
        // not leave the previous record's id for Send.
        if (tab === 'shared') {
          setSharedThreadId(null)
          sharedThreadIdRef.current = null
        }
        setMessages([])
      } finally {
        if (!cancelled) setLoadingThread(false)
        settleLoad()
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [open, canUse, tab, sharedEntityKey, personalThreadIdForLoad, refreshHistory])

  // Clear messages immediately when switching tabs so the prior thread doesn't flash.
  useEffect(() => {
    sharedFreshGenRef.current += 1
    cancel()
    setMessages([])
    setLastTurn(null)
    setStreamError(null)
    setLoadError(null)
    setSharedRemoteDeleted(false)
  }, [tab, setStreamError, cancel])

  useEffect(() => {
    if (open && tab === 'personal') {
      void refreshHistory()
    }
  }, [open, tab, refreshHistory])

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ behavior: 'smooth' })
  }, [messages, streaming])

  const startSharedFresh = useCallback(async () => {
    if (!sharedEntityKey) return
    const requestKey = sharedEntityKey
    const gen = ++sharedFreshGenRef.current
    const stillCurrent = () =>
      gen === sharedFreshGenRef.current &&
      tabRef.current === 'shared' &&
      sharedEntityKeyRef.current === requestKey
    cancel()
    setSharedRemoteDeleted(false)
    setStreamError(null)
    setLastTurn(null)
    setLoadError(null)
    setMessages([])
    setLoadingThread(true)
    try {
      const separator = sharedEntityKey.indexOf(':')
      const entityType = sharedEntityKey.slice(0, separator)
      const entityId = sharedEntityKey.slice(separator + 1)
      const thread = await getSharedThreadApi(entityType, entityId)
      if (!stillCurrent()) return
      setSharedThreadId(thread.cuid)
      sharedThreadIdRef.current = thread.cuid
      const msgs = await getChatMessagesApi(thread.cuid)
      if (!stillCurrent()) return
      applyLoadedMessages(msgs)
    } catch (err) {
      if (!stillCurrent()) return
      setLoadError(err instanceof Error ? err.message : String(err))
      setMessages([])
    } finally {
      if (stillCurrent()) setLoadingThread(false)
    }
  }, [sharedEntityKey, cancel, setStreamError])

  const markSharedRemoteDeleted = useCallback(() => {
    cancel()
    setSharedRemoteDeleted(true)
    setStreamError(null)
    setLastTurn(null)
  }, [cancel, setStreamError])

  /** Light check: if another user soft-deleted the shared thread, getOrCreate returns a new cuid. */
  const checkSharedThreadAlive = useCallback(async () => {
    if (!open || tab !== 'shared' || !sharedEntityKey || sharedRemoteDeleted) return
    const currentId = sharedThreadIdRef.current
    if (!currentId) return
    const gen = sharedFreshGenRef.current
    const stillValid = () =>
      gen === sharedFreshGenRef.current &&
      tabRef.current === 'shared' &&
      sharedThreadIdRef.current === currentId &&
      sharedEntityKeyRef.current === sharedEntityKey
    try {
      const separator = sharedEntityKey.indexOf(':')
      const entityType = sharedEntityKey.slice(0, separator)
      const entityId = sharedEntityKey.slice(separator + 1)
      const thread = await getSharedThreadApi(entityType, entityId)
      if (!stillValid()) return
      if (thread.cuid !== currentId) {
        markSharedRemoteDeleted()
      }
    } catch {
      // Ignore transient failures — send/list will surface deletion.
    }
  }, [open, tab, sharedEntityKey, sharedRemoteDeleted, markSharedRemoteDeleted])

  useEffect(() => {
    if (!open || tab !== 'shared') return
    const onVis = () => {
      if (document.visibilityState === 'visible') void checkSharedThreadAlive()
    }
    const onFocus = () => {
      void checkSharedThreadAlive()
    }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('focus', onFocus)
    return () => {
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('focus', onFocus)
    }
  }, [open, tab, checkSharedThreadAlive])

  // Drop remote-deleted notice when the focused record changes.
  useEffect(() => {
    setSharedRemoteDeleted(false)
  }, [sharedEntityKey])

  const handleSelectHistory = (summary: ChatThreadSummary) => {
    if (streaming) return
    setHistoryOpen(false)
    if (tab === 'personal' && summary.cuid === personalThreadId) return
    conversationEpochRef.current += 1
    switchTab('personal')
    setStreamError(null)
    setLastTurn(null)
    setMessages([])
    setPersonalThreadId(summary.cuid)
  }

  const handleNewChat = async () => {
    if (tab !== 'personal' || streaming) return
    try {
      await archivePersonalThreadApi()
      const thread = await getPersonalThreadApi()
      conversationEpochRef.current += 1
      personalThreadIdRef.current = thread.cuid
      setPersonalThreadId(thread.cuid)
      setMessages([])
      setLastTurn(null)
      setStreamError(null)
      setHistoryOpen(false)
      await refreshHistory()
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err))
    }
  }

  const beginRenameHistory = (summary: ChatThreadSummary) => {
    if (streaming) return
    skipRenameCommitRef.current = false
    renamingThreadIdRef.current = summary.cuid
    setRenamingThreadId(summary.cuid)
    setRenameDraft(summary.title)
  }

  const discardRenameHistory = () => {
    skipRenameCommitRef.current = true
    renamingThreadIdRef.current = null
    setRenamingThreadId(null)
  }

  const commitRenameHistory = async (threadIdToRename: string, draft: string) => {
    if (skipRenameCommitRef.current) {
      skipRenameCommitRef.current = false
      return
    }
    // Bind commit to the field that blurred. beginRenameHistory updates the live
    // ref synchronously, so a stale blur must not PATCH the newly selected row.
    if (renamingThreadIdRef.current !== threadIdToRename) return
    const title = draft.replace(/\s+/g, ' ').trim()
    const previous = history.find((h) => h.cuid === threadIdToRename)?.title
    renamingThreadIdRef.current = null
    setRenamingThreadId(null)
    if (!title || title === previous) return
    try {
      await renamePersonalThreadApi(threadIdToRename, title)
      setHistory((prev) =>
        prev.map((row) => (row.cuid === threadIdToRename ? { ...row, title } : row))
      )
      await refreshHistory()
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err))
    }
  }

  const requestDeleteHistory = (summary: ChatThreadSummary) => {
    if (streaming || deletingHistory) return
    setDeleteConfirm({ kind: 'personal', summary })
  }

  const requestDeleteShared = () => {
    if (streaming || deletingHistory || !sharedThreadId || sharedRemoteDeleted) return
    setDeleteConfirm({ kind: 'shared', threadId: sharedThreadId })
  }

  const handleConfirmDelete = async () => {
    if (!deleteConfirm || streaming || deletingHistory) return
    setDeletingHistory(true)
    try {
      if (deleteConfirm.kind === 'personal') {
        const summary = deleteConfirm.summary
        await deletePersonalThreadApi(summary.cuid)
        // Use the live ref — not the render-time personalThreadId — so a conversation
        // switch during the delete request does not wipe the user's new selection.
        if (personalThreadIdRef.current === summary.cuid) {
          const thread = await getPersonalThreadApi()
          if (personalThreadIdRef.current === summary.cuid) {
            conversationEpochRef.current += 1
            setPersonalThreadId(thread.cuid)
            personalThreadIdRef.current = thread.cuid
            setMessages([])
            setLastTurn(null)
            setStreamError(null)
          }
        }
        setHistoryOpen(false)
        await refreshHistory()
      } else {
        await deleteSharedThreadApi(deleteConfirm.threadId)
        setDeleteConfirm(null)
        await startSharedFresh()
        return
      }
      setDeleteConfirm(null)
    } catch (err) {
      if (isChatThreadDeletedError(err)) {
        if (deleteConfirm.kind === 'shared') {
          markSharedRemoteDeleted()
        }
        setDeleteConfirm(null)
      } else {
        setLoadError(err instanceof Error ? err.message : String(err))
      }
    } finally {
      setDeletingHistory(false)
    }
  }

  const runTurn = async (
    text: string,
    assistantId: string,
    options?: {
      retry?: boolean
      kind?: TabKind
      threadId?: string | null
      pageContext?: typeof enrichedPageContext
    }
  ) => {
    const sendKind = options?.kind ?? tabRef.current
    const sendThreadId =
      options?.threadId !== undefined
        ? options.threadId
        : sendKind === 'shared'
          ? sharedThreadIdRef.current
          : personalThreadIdRef.current
    const sendPageContext = options?.pageContext ?? enrichedPageContext
    const result = await send({
      threadId: sendThreadId ?? undefined,
      message: text,
      kind: sendKind,
      pageContext: sendPageContext,
      retry: options?.retry ? true : undefined,
      onUserPersisted: () => {
        userPersistedRef.current = true
      },
      onThread: (id) => {
        if (sendKind === 'shared') {
          sharedThreadIdRef.current = id
          setSharedThreadId(id)
        } else {
          personalThreadIdRef.current = id
          setPersonalThreadId(id)
        }
      },
      onAssistantDelta: (delta) => {
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, body: m.body + delta, pending: true } : m))
        )
      },
      onTool: (name) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                ...m,
                toolsUsed: [...(m.toolsUsed ?? []), name].filter((v, i, a) => a.indexOf(v) === i),
              }
              : m
          )
        )
      },
      onComplete: (toolsUsed) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                ...m,
                pending: false,
                status: undefined,
                toolsUsed: toolsUsed.length ? toolsUsed : m.toolsUsed,
                body: m.body || '(No response)',
              }
              : m
          )
        )
        setLastTurn(null)
        void refreshHistory()
      },
      onAbort: () => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                ...m,
                pending: false,
                status: 'stopped',
                body: m.body.trim() ? m.body : 'Generation stopped.',
              }
              : m
          )
        )
      },
    })

    if (result === 'thread_deleted') {
      if (sendKind === 'shared') {
        markSharedRemoteDeleted()
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  pending: false,
                  status: 'error',
                  body: m.body.trim() ? m.body : 'Conversation was deleted.',
                }
              : m
          )
        )
        return
      }
      // Drop the failed local turn before swapping threads — applyLoadedMessages
      // would otherwise re-attach it because status === 'error'.
      setStreamError(null)
      setLastTurn(null)
      setMessages([])
      const deletedThreadId = sendThreadId ?? personalThreadIdRef.current
      const recoveryEpoch = conversationEpochRef.current
      try {
        const thread = await getPersonalThreadApi()
        // Keep a history pick or new chat. Still replace the deleted id if the user
        // only left Just me, so coming back is not stuck on the deleted thread.
        if (
          conversationEpochRef.current !== recoveryEpoch ||
          personalThreadIdRef.current !== deletedThreadId
        ) {
          return
        }
        conversationEpochRef.current += 1
        setPersonalThreadId(thread.cuid)
        personalThreadIdRef.current = thread.cuid
      } catch (err) {
        if (
          conversationEpochRef.current !== recoveryEpoch ||
          personalThreadIdRef.current !== deletedThreadId
        ) {
          return
        }
        setLoadError(err instanceof Error ? err.message : String(err))
      }
      return
    }

    if (result === 'error') {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? {
              ...m,
              pending: false,
              status: 'error',
              body: m.body.trim() ? m.body : 'Something went wrong.',
            }
            : m
        )
      )
    }
  }

  const handleSend = async () => {
    const text = draft.trim()
    if (!text || streaming || sendInFlightRef.current) return
    if (tab === 'shared' && (sharedRemoteDeleted || !enrichedPageContext.sharedEntity)) return

    sendInFlightRef.current = true
    const sendTab = tab
    const sendPersonalThreadId = personalThreadIdRef.current
    const sendSharedEntityKey = sharedEntityKeyRef.current
    const sendConversationEpoch = conversationEpochRef.current
    const sendPageContext = enrichedPageContext
    const sendFallbackTenantId = pageContext.params.tenantId
    setDraft('')
    setStreamError(null)

    try {
      let load = threadLoadRef.current
      for (;;) {
        await load
        if (threadLoadRef.current === load) break
        load = threadLoadRef.current
      }
      if (tabRef.current !== sendTab) {
        setDraft((current) => current || text)
        return
      }
      // After the wait, compare live refs. `sharedEntityKey` is from this render, so a
      // record change during load is invisible. A null personal id would skip history changes.
      if (
        (sendTab === 'personal' &&
          (conversationEpochRef.current !== sendConversationEpoch ||
            (sendPersonalThreadId != null && personalThreadIdRef.current !== sendPersonalThreadId))) ||
        (sendTab === 'shared' && sharedEntityKeyRef.current !== sendSharedEntityKey)
      ) {
        setDraft((current) => current || text)
        return
      }

      const turnTenantId = resolveAccountLinkTenantId({
        pageContext: sendPageContext,
        fallbackTenantId: sendFallbackTenantId,
      })
      const nowIso = new Date().toISOString()
      const userMsg: DisplayMessage = {
        id: `local-user-${Date.now()}`,
        role: 'user',
        body: text,
        linkTenantId: turnTenantId,
        createdAt: nowIso,
      }
      const assistantId = `local-assistant-${Date.now()}`
      setLastTurn({
        userMessageId: userMsg.id,
        assistantMessageId: assistantId,
        text,
      })
      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          id: assistantId,
          role: 'assistant',
          body: '',
          pending: true,
          toolsUsed: [],
          linkTenantId: turnTenantId,
          createdAt: nowIso,
        },
      ])
      userPersistedRef.current = false

      await runTurn(text, assistantId, {
        kind: sendTab,
        threadId: sendTab === 'shared' ? sharedThreadIdRef.current : personalThreadIdRef.current,
        pageContext: sendPageContext,
      })
    } finally {
      sendInFlightRef.current = false
    }
  }

  const handleRetry = async () => {
    if (!lastTurn || streaming) return
    if (tab === 'shared' && !enrichedPageContext.sharedEntity) return

    const { text, assistantMessageId } = lastTurn
    setStreamError(null)
    setMessages((prev) =>
      prev.map((m) =>
        m.id === assistantMessageId
          ? { ...m, body: '', pending: true, status: undefined, toolsUsed: [] }
          : m
      )
    )
    await runTurn(text, assistantMessageId, { retry: userPersistedRef.current })
  }

  if (!canUse) return null

  return (
    <>
      {docked && open && (
        <div
          className="h-screen shrink-0 transition-[width] duration-300 ease-in-out"
          style={{ width: drawerWidth }}
          aria-hidden
        />
      )}
      <div className="fixed bottom-6 z-40" style={{ right: fabRightOffset + 24 }}>
        <ChatTooltip content="Sage chat (⌘J)" placement="left">
          <button
            type="button"
            aria-label="Open Sage chat"
            onClick={() => setOpen(true)}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg transition-all duration-200 ease-out hover:scale-105 hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400 active:scale-95"
          >
            <MessagingIcon />
          </button>
        </ChatTooltip>
      </div>

      {/* Non-modal panel: page stays interactive; hide via button, Esc, or ⌘J (not outside click). */}
      <Transition show={open}>
        <div className="pointer-events-none fixed inset-y-0 right-0 z-50 flex max-w-full transition duration-300 ease-in-out data-[closed]:translate-x-full">
          <div
            role="dialog"
            aria-modal="false"
            aria-label="Sage chat"
            className={`pointer-events-auto relative flex h-full min-w-0 max-w-full flex-col overflow-x-hidden bg-white dark:bg-slate-900${
              docked ? '' : ' shadow-xl shadow-slate-400/40 dark:shadow-slate-950'
            }`}
            style={{ width: drawerWidth }}
          >
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize Sage chat"
                title="Drag to resize"
                onPointerDown={onResizePointerDown}
                className="absolute inset-y-0 left-0 z-10 w-1.5 cursor-col-resize touch-none bg-transparent transition-colors duration-150 hover:bg-indigo-400/40 active:bg-indigo-500/50"
              />
              <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2.5 dark:border-slate-700">
                <h2 className="m-0 text-sm font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                  Sage chat
                </h2>
                <div className="flex items-center gap-0.5">
                  <CopyIconButton
                    label="Copy chat"
                    icon="chat"
                    variant="header"
                    disabled={!messages.some((m) => m.body.trim())}
                    onCopy={() => copyText(formatChatForCopy(messages))}
                  />
                  {tab === 'shared' && sharedAvailable && (
                    <>
                      <CopyIconButton
                        label="Copy link to shared chat"
                        icon="link"
                        variant="header"
                        onCopy={() =>
                          copyText(buildSageSharedDeepLink(location.pathname, location.search))
                        }
                      />
                      <ChatTooltip content="Delete conversation">
                        <button
                          type="button"
                          aria-label="Delete shared conversation"
                          disabled={!sharedThreadId || sharedRemoteDeleted || streaming || deletingHistory}
                          onClick={() => requestDeleteShared()}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors duration-150 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-400 dark:hover:bg-red-950/40 dark:hover:text-red-300"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </ChatTooltip>
                    </>
                  )}
                  {tab === 'personal' && (
                    <>
                      <div className="relative" ref={historyMenuRef}>
                        <ChatTooltip content="Conversation history">
                          <button
                            type="button"
                            onClick={() => setHistoryOpen((prev) => !prev)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors duration-150 hover:bg-slate-200/80 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100"
                            aria-expanded={historyOpen}
                            aria-label="Open conversation history"
                          >
                            <ClockIcon className="h-4 w-4" />
                          </button>
                        </ChatTooltip>
                        <Transition show={historyOpen}>
                          <div className="absolute right-0 z-20 mt-1 max-h-72 w-80 origin-top-right overflow-x-hidden overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 text-slate-900 shadow-lg transition duration-150 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100">
                            {history.length === 0 && (
                              <div className="px-3 py-2 text-xs text-slate-500">No conversations yet</div>
                            )}
                            {history.map((row) => {
                              const when = formatThreadWhen(row.modifiedAt || row.createdAt)
                              const isRenaming = renamingThreadId === row.cuid
                              return (
                                <div
                                  key={row.cuid}
                                  className={`group flex w-full min-w-0 items-stretch gap-0.5 rounded-md px-1 py-0.5 transition-colors duration-150 hover:bg-slate-100 dark:hover:bg-slate-700 ${
                                    row.cuid === threadId ? 'bg-indigo-50 hover:bg-indigo-100/80 dark:bg-indigo-950/40 dark:hover:bg-indigo-950/60' : ''
                                  }`}
                                >
                                  {isRenaming ? (
                                    <form
                                      className="flex min-w-0 flex-1 items-center gap-1 px-2 py-1"
                                      onSubmit={(e) => {
                                        e.preventDefault()
                                        void commitRenameHistory(row.cuid, renameDraft)
                                      }}
                                    >
                                      <input
                                        autoFocus
                                        value={renameDraft}
                                        onChange={(e) => setRenameDraft(e.target.value)}
                                        onBlur={(e) =>
                                          void commitRenameHistory(row.cuid, e.currentTarget.value)
                                        }
                                        onKeyDown={(e) => {
                                          if (e.key === 'Escape') {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            discardRenameHistory()
                                          }
                                        }}
                                        aria-label="Conversation title"
                                        className="min-w-0 flex-1 rounded border border-indigo-300 bg-white px-1.5 py-0.5 text-xs text-slate-900 outline-none focus:ring-1 focus:ring-indigo-400 dark:border-indigo-600 dark:bg-slate-900 dark:text-slate-100"
                                      />
                                    </form>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => handleSelectHistory(row)}
                                      className="flex min-w-0 flex-1 flex-col gap-0.5 rounded-md px-2 py-1.5 text-left"
                                    >
                                      <span className="break-words text-xs font-medium leading-snug">
                                        {row.title}
                                      </span>
                                      {when.absolute && (
                                        <ChatTooltip content={when.absolute} placement="left">
                                          <span className="cursor-default text-[11px] text-slate-400 dark:text-slate-500">
                                            {when.relative}
                                            <span className="text-slate-300 dark:text-slate-600"> · </span>
                                            {when.date}
                                          </span>
                                        </ChatTooltip>
                                      )}
                                    </button>
                                  )}
                                  <div className="flex shrink-0 items-center gap-0.5 pr-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                                    <ChatTooltip content="Rename" placement="left">
                                      <button
                                        type="button"
                                        aria-label="Rename conversation"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          beginRenameHistory(row)
                                        }}
                                        className="inline-flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-slate-600 dark:hover:text-slate-100"
                                      >
                                        <PencilSquareIcon className="h-3.5 w-3.5" />
                                      </button>
                                    </ChatTooltip>
                                    <ChatTooltip content="Delete" placement="left">
                                      <button
                                        type="button"
                                        aria-label="Delete conversation"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          requestDeleteHistory(row)
                                        }}
                                        className="inline-flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 dark:hover:text-red-300"
                                      >
                                        <TrashIcon className="h-3.5 w-3.5" />
                                      </button>
                                    </ChatTooltip>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </Transition>
                      </div>
                      <ChatTooltip content="Start a new personal chat">
                        <button
                          type="button"
                          onClick={() => void handleNewChat()}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors duration-150 hover:bg-slate-200/80 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100"
                          aria-label="Start a new personal chat"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                      </ChatTooltip>
                    </>
                  )}
                  <ChatTooltip content={docked ? 'Float over page' : 'Dock to side'}>
                    <button
                      type="button"
                      onClick={toggleDocked}
                      aria-label={docked ? 'Float over page' : 'Dock to side'}
                      aria-pressed={docked}
                      className={`inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors duration-150 hover:bg-slate-200/80 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100 ${
                        docked ? 'bg-slate-200/80 text-slate-800 dark:bg-slate-700 dark:text-slate-100' : ''
                      }`}
                    >
                      <ViewColumnsIcon className="h-4 w-4" />
                    </button>
                  </ChatTooltip>
                  <ChatTooltip content="Hide chat (Esc)">
                    <button
                      type="button"
                      onClick={() => {
                        setHistoryOpen(false)
                        setOpen(false)
                      }}
                      aria-label="Hide chat"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors duration-150 hover:bg-slate-200/80 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100"
                    >
                      <ChevronDoubleRightIcon className="h-4 w-4" />
                    </button>
                  </ChatTooltip>
                </div>
              </div>

              <div className="border-b border-slate-200 dark:border-slate-700">
                {showModeTip && canShared && (
                  <div className="mx-3 mt-2 mb-1 flex items-start gap-2 rounded-md bg-slate-100 px-2.5 py-2 text-[11px] leading-snug text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                    <p className="min-w-0 flex-1">
                      <span className="font-semibold">Just me</span> follows you.{' '}
                      <span className="font-semibold">This record</span> is a shared team
                      thread for the focused tenant, account, or program.
                    </p>
                    <button
                      type="button"
                      onClick={dismissModeTip}
                      className="shrink-0 rounded px-1.5 py-0.5 font-medium text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-700"
                      aria-label="Dismiss tip"
                    >
                      Got it
                    </button>
                  </div>
                )}
                <div
                  role="tablist"
                  aria-label="Chat thread type"
                  className="flex gap-0 px-3"
                >
                  <ChatTooltip content="Just me — private thread that follows you across pages.">
                    <button
                      type="button"
                      role="tab"
                      id="sage-chat-tab-personal"
                      aria-selected={tab === 'personal'}
                      aria-controls="sage-chat-panel"
                      onClick={() => switchTab('personal')}
                      className={`-mb-px border-b px-2.5 py-2 text-[13px] font-medium transition-colors duration-150 ${tab === 'personal'
                        ? 'border-indigo-500 text-slate-900 dark:border-indigo-400 dark:text-slate-100'
                        : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                        }`}
                    >
                      Just me
                    </button>
                  </ChatTooltip>
                  {canShared && (
                    <ChatTooltip
                      content={
                        sharedAvailable
                          ? `Team thread for this record${sharedRecordLabel ? ` (${sharedRecordLabel})` : ''}`
                          : 'Open a tenant, account, or program first'
                      }
                    >
                      <span className="inline-flex max-w-[20rem]">
                        <button
                          type="button"
                          role="tab"
                          id="sage-chat-tab-shared"
                          aria-selected={tab === 'shared'}
                          aria-controls="sage-chat-panel"
                          disabled={!sharedAvailable}
                          title={sharedTabLabel}
                          onClick={() => sharedAvailable && switchTab('shared')}
                          className={`-mb-px max-w-full truncate border-b px-2.5 py-2 text-[13px] font-medium transition-colors duration-150 ${tab === 'shared'
                            ? 'border-indigo-500 text-slate-900 dark:border-indigo-400 dark:text-slate-100'
                            : sharedAvailable
                              ? 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                              : 'cursor-not-allowed border-transparent text-slate-400 dark:text-slate-600'
                            }`}
                        >
                          {sharedTabLabel}
                        </button>
                      </span>
                    </ChatTooltip>
                  )}
                </div>
                {canShared && !sharedAvailable && (
                  <p className="mx-3 mb-2 text-[11px] leading-snug text-slate-500 dark:text-slate-400">
                    Open a tenant, account, or program to use This record.
                  </p>
                )}
              {deepLinkNotice && (
                <div className="mx-4 mb-2 flex items-start gap-2 rounded-md bg-amber-50 px-2.5 py-2 text-[11px] leading-snug text-amber-950 dark:bg-amber-950/40 dark:text-amber-100">
                  <p className="min-w-0 flex-1">{deepLinkNotice}</p>
                  <button
                    type="button"
                    onClick={() => setDeepLinkNotice(null)}
                    className="shrink-0 rounded px-1.5 py-0.5 font-medium text-amber-800 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/50"
                    aria-label="Dismiss notice"
                  >
                    Dismiss
                  </button>
                </div>
              )}
              {sharedRemoteDeleted && tab === 'shared' && (
                <div
                  role="status"
                  className="mx-4 mb-2 flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12px] leading-snug text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
                >
                  <p>This conversation was deleted by another user.</p>
                  <button
                    type="button"
                    onClick={() => void startSharedFresh()}
                    className="self-start rounded-md bg-amber-700 px-2.5 py-1 text-[12px] font-medium text-white transition-colors hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-500"
                  >
                    Start new conversation
                  </button>
                </div>
              )}
            </div>

              <div className="border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                <ChatTooltip
                  content={contextExpanded ? 'Collapse context' : 'Expand context'}
                  placement="bottom"
                >
                  <button
                    type="button"
                    onClick={() => setContextExpanded((prev) => !prev)}
                    aria-expanded={contextExpanded}
                    aria-label={contextExpanded ? 'Collapse context' : 'Expand context'}
                    className="flex w-full items-start gap-2 px-3 py-2 text-left transition-colors duration-150 hover:bg-slate-50 dark:hover:bg-slate-800/80"
                  >
                    <ChevronDownIcon
                      className={`mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform duration-200 ease-out ${contextExpanded ? 'rotate-0' : '-rotate-90'
                        }`}
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Context
                      </div>
                      <div
                        className={`mt-0.5 break-words text-[13px] font-medium text-slate-900 transition-[max-height] duration-200 dark:text-slate-100 ${contextExpanded ? '' : 'truncate'
                          }`}
                      >
                        {viewContext.headline}
                      </div>
                      <div
                        className={`grid transition-[grid-template-rows] duration-200 ease-out ${contextExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                          }`}
                      >
                        <div className="min-h-0 overflow-hidden">
                          <div
                            className={`pt-0.5 transition-opacity duration-200 ease-out ${contextExpanded ? 'opacity-100' : 'opacity-0'
                              }`}
                          >
                            {viewContext.lines.map((line) => (
                              <div key={line} className="break-all text-xs text-slate-600 dark:text-slate-300">
                                {line}
                              </div>
                            ))}
                            <p className="mt-1.5 text-[11px] leading-snug text-slate-500 dark:text-slate-400">
                              Sage also attaches a bounded snapshot for this context (e.g. balances,
                              status, recent note titles).
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                </ChatTooltip>
              </div>

              <div
                id="sage-chat-panel"
                role="tabpanel"
                aria-labelledby={tab === 'personal' ? 'sage-chat-tab-personal' : 'sage-chat-tab-shared'}
                className="relative min-w-0 flex-1 space-y-4 overflow-x-hidden overflow-y-auto bg-white px-3 py-3 text-sm text-slate-900 dark:bg-slate-900 dark:text-slate-100"
              >
                {loadingThread && (
                  <p className="animate-pulse text-slate-500 transition-opacity duration-200">Loading thread…</p>
                )}
                {loadError && <p className="text-red-600 dark:text-red-400">{loadError}</p>}
                {messages.map((m) => {
                  const when = m.createdAt ? formatThreadWhen(m.createdAt) : null
                  if (m.pending && !m.body) {
                    return (
                      <div
                        key={m.id}
                        className="group flex flex-col items-start gap-1"
                        role="status"
                        aria-label="Sage is typing"
                      >
                        <div className="flex items-baseline gap-2 text-[11px] text-slate-500">
                          <span className="font-medium">Sage</span>
                          {when?.relative && (
                            <ChatTooltip content={when.absolute} placement="top">
                              <span className="cursor-default">{when.relative}</span>
                            </ChatTooltip>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 py-1">
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.3s] dark:bg-slate-500" />
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.15s] dark:bg-slate-500" />
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 dark:bg-slate-500" />
                        </div>
                      </div>
                    )
                  }
                  const isUser = m.role === 'user'
                  return (
                    <div
                      key={m.id}
                      className={`group flex min-w-0 flex-col gap-1 ${isUser ? 'items-end' : 'items-start'}`}
                    >
                      <div
                        className={`flex max-w-full items-center gap-2 text-[11px] text-slate-500 ${
                          isUser ? 'flex-row-reverse' : ''
                        }`}
                      >
                        <span className="font-medium">
                          {isUser ? 'You' : 'Sage'}
                          {m.authorEmail ? ` · ${m.authorEmail}` : ''}
                          {m.status === 'stopped' ? ' · Stopped' : ''}
                          {m.status === 'error' ? ' · Error' : ''}
                        </span>
                        {when?.relative && (
                          <ChatTooltip content={when.absolute} placement="top">
                            <span className="cursor-default">{when.relative}</span>
                          </ChatTooltip>
                        )}
                        <CopyIconButton
                          label="Copy message"
                          icon="message"
                          disabled={!m.body.trim()}
                          onCopy={() => copyText(formatMessageForCopy(m))}
                        />
                      </div>
                      <div
                        className={`max-w-full min-w-0 transition-[opacity,transform] duration-200 ease-out ${
                          isUser
                            ? 'rounded-2xl rounded-br-md bg-indigo-600 px-3 py-2 text-white dark:bg-indigo-500'
                            : 'px-0.5 py-0.5 text-slate-800 dark:text-slate-100'
                        }`}
                      >
                        {m.role === 'assistant' ? (
                          <ChatMarkdown linkTenantId={m.linkTenantId}>{m.body}</ChatMarkdown>
                        ) : (
                          <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-[13px] leading-relaxed">
                            {m.body}
                          </div>
                        )}
                      </div>
                      {!!m.toolsUsed?.length && (
                        <div className="mt-0.5 flex max-w-full min-w-0 flex-wrap items-center gap-1.5">
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                            Tools
                          </span>
                          {m.toolsUsed.map((tool) => (
                            <span
                              key={tool}
                              className="rounded-md bg-emerald-100 px-1.5 py-0.5 font-mono text-[11px] text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
                            >
                              {tool}
                            </span>
                          ))}
                        </div>
                      )}
                    {(m.status === 'stopped' || m.status === 'error') &&
                      !streaming &&
                      lastTurn?.assistantMessageId === m.id && (
                      <div className="mt-1">
                        <button
                          type="button"
                          onClick={() => void handleRetry()}
                          aria-label="Retry"
                          className="rounded-md bg-slate-200/80 px-2 py-1 text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
                        >
                          Retry
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
              {streamError && (
                <p className="text-sm text-red-600 dark:text-red-400">{streamError}</p>
              )}
              <div ref={bottomRef} />
            </div>

              <div className="border-t border-slate-200 p-3 dark:border-slate-700">
                <div className="flex items-end gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-2.5 py-2 shadow-sm focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-400 dark:border-slate-600 dark:bg-slate-800/80">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        if (!streaming) void handleSend()
                      }
                    }}
                    rows={2}
                    placeholder={
                      tab === 'shared' && !sharedAvailable
                        ? 'Open a tenant, account, or program first'
                        : messages.some((m) => m.body.trim())
                          ? 'Add a followup...'
                          : 'Ask Sage…'
                    }
                    disabled={tab === 'shared' && (sharedRemoteDeleted || !sharedAvailable)}
                    className="min-h-[48px] flex-1 resize-none border-0 bg-transparent px-1 py-1 text-[13px] text-slate-900 shadow-none outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 disabled:opacity-60 dark:text-slate-100"
                  />
                  {streaming ? (
                    <ChatTooltip content="Stop generating (Esc)" placement="top">
                      <span className="inline-flex">
                        <button
                          type="button"
                          onClick={() => cancel()}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-700 text-white transition-all duration-150 hover:bg-slate-600 active:scale-95 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-white"
                          aria-label="Stop generating"
                        >
                          <StopIcon className="h-4 w-4" />
                        </button>
                      </span>
                    </ChatTooltip>
                  ) : (
                    <ChatTooltip content="Send message" placement="top">
                      <span className="inline-flex">
                        <button
                          type="button"
                          onClick={() => void handleSend()}
                          disabled={
                            !draft.trim() ||
                            (tab === 'shared' && (sharedRemoteDeleted || !sharedAvailable))
                          }
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white transition-all duration-150 hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 active:scale-95"
                          aria-label="Send message"
                        >
                          <PaperAirplaneIcon className="h-4 w-4" />
                        </button>
                      </span>
                    </ChatTooltip>
                  )}
                </div>
              </div>
            </div>
          </div>
      </Transition>
      {deleteConfirm &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
            role="presentation"
            onClick={() => {
              if (!deletingHistory) setDeleteConfirm(null)
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="sage-delete-conversation-title"
              className="w-full max-w-sm space-y-4 rounded-xl bg-white p-5 shadow-xl dark:bg-slate-800"
              onClick={(e) => e.stopPropagation()}
            >
              <div>
                <h3
                  id="sage-delete-conversation-title"
                  className="text-base font-semibold text-slate-900 dark:text-slate-100"
                >
                  Delete conversation?
                </h3>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                  {deleteConfirm.kind === 'personal' ? (
                    <>
                      Delete “{deleteConfirm.summary.title}”? It will no longer appear in your
                      history.
                    </>
                  ) : (
                    <>
                      Soft-delete this shared conversation
                      {sharedRecordLabel ? ` for ${sharedRecordLabel}` : ''}? Teammates still viewing
                      it will be asked to start a new conversation.
                    </>
                  )}
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  disabled={deletingHistory}
                  onClick={() => setDeleteConfirm(null)}
                  className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-50 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={deletingHistory}
                  onClick={() => void handleConfirmDelete()}
                  className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-500 disabled:opacity-50"
                >
                  {deletingHistory ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  )
}

export default ChatDrawer
